import { z } from 'zod';
import { InvitePhoneSchema } from './api';

/**
 * The admin console (gap 2), the status page and help (gap 7).
 *
 *   GET    /api/v1/admin/me                               -> AdminMe               admin or grievance
 *   GET    /api/v1/admin/admins                           -> AdminList             admin
 *   POST   /api/v1/admin/admins          AddAdmin         -> AdminList             admin, fresh sign-in
 *   DELETE /api/v1/admin/admins/:userId/:role             -> AdminList             admin, fresh sign-in
 *   GET    /api/v1/admin/flags                            -> FlagList              admin
 *   POST   /api/v1/admin/flags           CreateFlag       -> FlagList              admin, fresh sign-in
 *   PUT    /api/v1/admin/flags/:key      UpdateFlag       -> FlagList              admin, fresh sign-in
 *   POST   /api/v1/admin/flags/:key/people  FlagPerson    -> FlagList              admin, fresh sign-in
 *   DELETE /api/v1/admin/flags/:key/people/:userId        -> FlagList              admin, fresh sign-in
 *   GET    /api/v1/admin/status                           -> AdminStatus           admin
 *   POST   /api/v1/admin/status-updates  PostStatusUpdate -> AdminStatus           admin
 *   GET    /api/v1/status                                 -> PublicStatus          no sign-in
 *
 * The admin token (outside production) counts as an admin with every role and a fresh sign-in.
 */

/** admin: everything in the console; grievance: privacy requests only (the Grievance Officer). */
export const ADMIN_ROLES = ['admin', 'grievance'] as const;
export const AdminRoleSchema = z.enum(ADMIN_ROLES);
export type AdminRole = z.infer<typeof AdminRoleSchema>;

/** A sign-in this recent counts as fresh, for breach notices, flags and admins (minutes; ADMIN_FRESH_SIGN_IN_MINUTES). */
export const FRESH_SIGN_IN_MINUTES = 10;

export const AdminMeSchema = z.object({
  user: z.object({ id: z.string(), name: z.string().nullable(), phoneMasked: z.string() }).nullable(),
  roles: z.array(AdminRoleSchema),
  /** sign_in: an admin signed in with their mobile; token: the admin token (laptops and tests). */
  via: z.enum(['sign_in', 'token']),
  /** Until when the sign-in counts as fresh (null with the token: always fresh). */
  freshUntil: z.string().nullable(),
  /** How long a sign-in counts as fresh, in minutes (10; shorter in browser tests). */
  freshMinutes: z.number(),
  /** For the home screen (X1). */
  counts: z.object({
    openRequests: z.number().int(),
    overdueRequests: z.number().int(),
    dueSoonRequests: z.number().int(),
    dueDateChanges: z.number().int(),
    flags: z.number().int(),
    admins: z.number().int(),
  }),
  /** The last check of every key (the Keys page), for "Keys and services": admins only; null if never run. */
  keys: z
    .object({
      ranAt: z.string(),
      fails: z.number().int(),
      warns: z.number().int(),
      /** The failing checks' names, e.g. "GSP sandbox". */
      failing: z.array(z.string()),
    })
    .nullable(),
});
export type AdminMe = z.infer<typeof AdminMeSchema>;

export const AdminPersonSchema = z.object({
  userId: z.string(),
  name: z.string().nullable(),
  phoneMasked: z.string(),
  role: AdminRoleSchema,
  addedAt: z.string(),
  addedBy: z.string().nullable(),
  you: z.boolean(),
});
export type AdminPerson = z.infer<typeof AdminPersonSchema>;

export const AdminListSchema = z.object({ admins: z.array(AdminPersonSchema) });
export type AdminList = z.infer<typeof AdminListSchema>;

export const AddAdminSchema = z.strictObject({ phone: InvitePhoneSchema, role: AdminRoleSchema });
export type AddAdmin = z.input<typeof AddAdminSchema>;

// ---------------------------------------------------------------------------
// Feature flags (X7)
// ---------------------------------------------------------------------------

export const FLAG_KEY = /^[a-z][a-z0-9_]{1,39}$/;

export const FlagSchema = z.object({
  key: z.string(),
  description: z.string(),
  /** On for everyone in this environment. */
  here: z.boolean(),
  /** And for these people. */
  people: z.array(z.object({ userId: z.string(), name: z.string().nullable(), phoneMasked: z.string() })),
  businesses: z.array(z.object({ businessId: z.string(), gstin: z.string(), name: z.string() })).default([]),
  updatedAt: z.string(),
});
export type Flag = z.infer<typeof FlagSchema>;

export const FlagListSchema = z.object({ env: z.string(), flags: z.array(FlagSchema) });
export type FlagList = z.infer<typeof FlagListSchema>;

export const CreateFlagSchema = z.strictObject({
  key: z.string().trim().regex(FLAG_KEY, { error: 'Small letters, digits and _ only, starting with a letter' }),
  description: z.string().trim().min(3).max(200),
});
export type CreateFlag = z.input<typeof CreateFlagSchema>;

export const UpdateFlagSchema = z.strictObject({ here: z.boolean() });
export const FlagBusinessSchema = z.strictObject({
  gstin: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/),
});
export const FlagPersonSchema = z.strictObject({ phone: InvitePhoneSchema });

// ---------------------------------------------------------------------------
// The status page (L2) and its updates (X9)
// ---------------------------------------------------------------------------

/** chat: chat and filing (EkApp itself); gst_portal: through the GSP; sign_in: SMS codes; email. */
export const STATUS_COMPONENTS = ['chat', 'gst_portal', 'sign_in', 'email'] as const;
export const StatusComponentSchema = z.enum(STATUS_COMPONENTS);
export type StatusComponent = z.infer<typeof StatusComponentSchema>;

export const STATUS_STATES = ['working', 'slow', 'down'] as const;
export const StatusStateSchema = z.enum(STATUS_STATES);
export type StatusState = z.infer<typeof StatusStateSchema>;

export const PostStatusUpdateSchema = z.strictObject({
  component: StatusComponentSchema,
  state: StatusStateSchema,
  message: z.string().trim().min(3).max(500),
});
export type PostStatusUpdate = z.input<typeof PostStatusUpdateSchema>;

export const StatusUpdateSchema = z.object({
  id: z.string(),
  component: StatusComponentSchema,
  state: StatusStateSchema,
  message: z.string(),
  postedAt: z.string(),
});
export type StatusUpdate = z.infer<typeof StatusUpdateSchema>;

export const PublicStatusSchema = z.object({
  /** The worst of the components. */
  state: StatusStateSchema,
  /** What's wrong, when something is: the worst part, and the newest update's words about it (if any). */
  problem: z
    .object({ component: StatusComponentSchema, state: StatusStateSchema, message: z.string().nullable() })
    .nullable(),
  components: z.array(z.object({ component: StatusComponentSchema, state: StatusStateSchema })),
  /** The last 30 days, newest first. */
  updates: z.array(StatusUpdateSchema),
  checkedAt: z.string(),
});
export type PublicStatus = z.infer<typeof PublicStatusSchema>;

/** The admin's view (X9): with what EkApp measured for each part, in a sentence. */
export const AdminStatusSchema = PublicStatusSchema.extend({
  chatAi: z
    .object({
      month: z.string(),
      spentInr: z.number(),
      budgetInr: z.number(),
      state: z.enum(['ok', 'warning', 'paused']),
    })
    .optional(),
  components: z.array(
    z.object({
      component: StatusComponentSchema,
      state: StatusStateSchema,
      /** What the checks found, e.g. "2 of 5 calls took over 10 s in the last 15 minutes". */
      measured: z.string(),
      /** What the checks alone say (the state can be worse when an admin's update says so). */
      measuredState: StatusStateSchema,
    }),
  ),
});
export type AdminStatus = z.infer<typeof AdminStatusSchema>;

// ---------------------------------------------------------------------------
// Help (L1): support's contact, from settings
// ---------------------------------------------------------------------------

export const SupportContactSchema = z.object({
  email: z.string().nullable(),
  phone: z.string().nullable(),
  hours: z.string().nullable(),
});
export type SupportContact = z.infer<typeof SupportContactSchema>;
